import {
  type FC,
  Fragment,
  type ReactNode,
  type Ref,
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { truthyPredicate } from '@iconik-io/core/src/truthy_predicate';
import { Button, Icon } from '@iconik-io/uad';

import {
  MultiSelectFilterItemChip,
  MultiSelectFilterSummaryChip,
} from './multi_select_filter_chip';
import { MultiSelectFilterModifierDropdown } from './multi_select_filter_modifier_dropdown';
import { MultiSelectFilterSelectionPopover } from './multi_select_filter_selection_popover';
import type { MultiSelectFilterRowProps } from './types';

import styles from './multi_select_filter_row.module.css';

/**
 * Determines whether all chips fit in the available width by measuring a hidden
 * container that always renders every individual chip. When the visible
 * container shows a summary chip instead, the hidden one still has the real
 * chips so we can detect when there is enough room to expand back.
 */
function useChipsOverflow(
  visibleRef: RefObject<HTMLDivElement | null>,
  measureRef: RefObject<HTMLDivElement | null>,
  chipCount: number,
) {
  const [allFit, setAllFit] = useState(true);

  const measure = useCallback(() => {
    const visibleContainer = visibleRef.current;
    const measureContainer = measureRef.current;
    if (!visibleContainer || !measureContainer || chipCount === 0) {
      setAllFit(true);
      return;
    }

    setAllFit(measureContainer.scrollWidth <= visibleContainer.clientWidth);
  }, [visibleRef, measureRef, chipCount]);

  useLayoutEffect(() => {
    measure();
  }, [measure]);

  useEffect(() => {
    const container = visibleRef.current;
    if (!container) {
      return;
    }
    const observer = new ResizeObserver(() => {
      measure();
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [visibleRef, measure]);

  return allFit;
}

type MeasureContainerProps = {
  measureRef: Ref<HTMLDivElement>;
  children: ReactNode;
};

function ChipMeasureContainer({ measureRef, children }: MeasureContainerProps) {
  return (
    <div
      ref={measureRef}
      aria-hidden="true"
      className={styles.measure_container}
    >
      {children}
    </div>
  );
}

export const MultiSelectFilterRow: FC<MultiSelectFilterRowProps> = ({
  group,
  rowIndex,
  isLastRow,
  isFirstRow,
  canAddRow,
  showAddButton,
  resolvedItems,
  itemLabels,
  renderItemChip,
  availableModifiers,
  disabled,
  showItemAvatar,
  onModifierChange,
  onItemsChange,
  onClearRow,
  onAddRow,
  loadOptions,
  preloadedOptions,
  isPreloading,
  showStartTypingHint,
  startTypingHint,
}) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const rowRef = useRef<HTMLDivElement>(null);
  const chipsAreaRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);

  const handleModifierChange = (modifier: typeof group.modifier) => {
    onModifierChange(rowIndex, modifier);
  };

  const handleSelectionChange = (selectedIds: string[]) => {
    onItemsChange(rowIndex, selectedIds);
  };

  const handleChipsAreaClick = () => {
    if (!disabled) {
      setIsPopoverOpen(true);
    }
  };

  const resolvedChipItems = group.items
    .map((itemId) => resolvedItems.get(itemId))
    .filter(truthyPredicate);

  const hasItems = group.items.length > 0;
  const showClearButton = !disabled && (isFirstRow ? hasItems : true);

  const allChipsFit = useChipsOverflow(
    chipsAreaRef,
    measureRef,
    resolvedChipItems.length,
  );
  const showSummary = !allChipsFit && resolvedChipItems.length > 0;

  const chipElements = resolvedChipItems.map((item) => (
    <Fragment key={item.id}>
      {renderItemChip ? (
        renderItemChip(item)
      ) : (
        <MultiSelectFilterItemChip
          item={item}
          showItemAvatar={showItemAvatar}
        />
      )}
    </Fragment>
  ));

  return (
    <div className={styles.row_wrapper}>
      <div ref={rowRef} className={styles.row}>
        {hasItems && (
          <MultiSelectFilterModifierDropdown
            modifier={group.modifier}
            itemCount={group.items.length}
            availableModifiers={availableModifiers}
            onChange={handleModifierChange}
            isDisabled={disabled}
            triggerRef={rowRef}
          />
        )}
        <div
          ref={chipsAreaRef}
          className={styles.chips_area}
          onClick={handleChipsAreaClick}
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-disabled={disabled || undefined}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              handleChipsAreaClick();
            }
          }}
          aria-label={`Select ${itemLabels.plural}`}
        >
          {resolvedChipItems.length === 0 && (
            <span className={styles.chips_area_empty}>
              {hasItems || isPreloading
                ? 'Loading...'
                : `Select ${itemLabels.singular}(s)...`}
            </span>
          )}
          {showSummary ? (
            <MultiSelectFilterSummaryChip
              count={resolvedChipItems.length}
              itemLabels={itemLabels}
            />
          ) : (
            chipElements
          )}
        </div>
        {resolvedChipItems.length > 0 && (
          <ChipMeasureContainer measureRef={measureRef}>
            {chipElements}
          </ChipMeasureContainer>
        )}
        {showClearButton && (
          <Button
            icon={<Icon name="close" size="s" />}
            variant="tertiary"
            size="s"
            className={styles.clear_button}
            onPress={() => onClearRow(rowIndex)}
            aria-label={isFirstRow ? 'Clear selection' : 'Remove row'}
          />
        )}
      </div>
      {isLastRow && showAddButton && (
        <div className={styles.side_action}>
          <Button
            icon={<Icon name="plus" size="s" />}
            variant="tertiary"
            size="s"
            className={styles.add_button}
            onPress={onAddRow}
            isDisabled={disabled || !canAddRow}
            aria-label="Add condition"
          />
        </div>
      )}
      {!isLastRow && <span className={styles.connector}>and</span>}
      {isPopoverOpen && (
        <MultiSelectFilterSelectionPopover
          triggerRef={rowRef}
          selectedItemIds={group.items}
          resolvedItems={resolvedItems}
          loadOptions={loadOptions}
          initialOptions={isPreloading ? undefined : preloadedOptions}
          onSelectionChange={handleSelectionChange}
          onClose={() => setIsPopoverOpen(false)}
          showItemAvatar={showItemAvatar}
          showStartTypingHint={showStartTypingHint}
          startTypingHint={startTypingHint}
        />
      )}
    </div>
  );
};
